/**
 * <ado-quick-add>: AJAX add-to-cart for snippets/ado-quick-add.liquid.
 *
 * The element wraps a normal <form action="/cart/add">, so without JS the form simply posts and Shopify
 * redirects to the cart. With JS:
 *   POST {cart_add_url}.js → GET {cart_url}.js → document 'ado:cart:updated' {detail:{item_count}}
 *   data-after="toast"   stay on the page: the button shows "Added" with an oat burst, the card's tube flies into
 *                        the nav bag (flyToCart in @theme/ado-motion), the badge bumps as it lands, then the shared
 *                        toast "Added <title> · View cart" slides in. While the bag has no kit and fewer than all
 *                        three steps, the toast adds one line: "Get all 3 as the Tween's Kit · Save ₹… →"
 *                        (data-kit-url / data-kit-title / data-kit-saving / data-kit-handle from the snippet).
 *   data-after="cart"    navigate to the cart
 *   data-after="section" do nothing: the form carries data-ado-cart-add and sections/cart.liquid handles it
 * Double submits are blocked with aria-disabled; errors show in the form's inline role="alert" line.
 * Under reduced motion there is no flight or burst: the badge and the toast update at once.
 *
 * Also imported as @theme/ado-quick-add by assets/ado-agent-cart.js (ensureToast, showToast, stepKey), so ado AI's
 * adds reuse the same toast. Its styles live in assets/ado-ui.css for that reason.
 */

const TOAST_MS = 5000;
const ADDED_MS = 2000;
const FALLBACK_ERROR = 'Sorry, we could not add this to your cart. Please try again.';

/** @type {HTMLElement | undefined} */
let toast;
/** @type {number | undefined} */
let toastTimer;

function reducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Creates the one shared live region early (empty), so screen readers announce it when it fills. */
export function ensureToast() {
  if (toast && toast.isConnected) return toast;
  toast = document.createElement('div');
  toast.className = 'ado-toast';
  toast.id = 'ado-toast';
  toast.innerHTML = `
    <div class="ado-toast__inner">
      <span class="ado-toast__thumb" aria-hidden="true"></span>
      <div class="ado-toast__text" role="status" aria-live="polite" aria-atomic="true">
        <p class="ado-toast__msg"></p>
      </div>
      <a class="ado-toast__link ado-btn ado-btn--sm" href="/cart">View cart<svg class="ado-arrow" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 12h13M13 6.5 18.5 12 13 17.5"/></svg></a>
      <button type="button" class="ado-toast__close" aria-label="Dismiss">
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6.5 6.5l11 11M17.5 6.5l-11 11"/></svg>
      </button>
      <a class="ado-toast__nudge" href="/" hidden>
        <span class="ado-toast__nudge-text"></span>
        <span class="ado-toast__nudge-save"></span>
        <svg class="ado-arrow" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 12h13M13 6.5 18.5 12 13 17.5"/></svg>
      </a>
    </div>`;
  const schedule = (/** @type {number} */ ms) => {
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(hideToast, ms);
  };
  toast.addEventListener('mouseenter', () => window.clearTimeout(toastTimer));
  toast.addEventListener('mouseleave', () => {
    if (toast?.classList.contains('is-visible') && !toast.contains(document.activeElement)) schedule(2500);
  });
  toast.addEventListener('focusin', () => window.clearTimeout(toastTimer));
  toast.addEventListener('focusout', (event) => {
    const next = /** @type {FocusEvent} */ (event).relatedTarget;
    if (toast?.classList.contains('is-visible') && !(next instanceof Node && toast.contains(next))) schedule(2500);
  });
  toast.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') hideToast();
  });
  toast.querySelector('.ado-toast__close')?.addEventListener('click', hideToast);
  document.body.append(toast);
  return toast;
}

function hideToast() {
  window.clearTimeout(toastTimer);
  if (!toast) return;
  const hadFocus = toast.contains(document.activeElement);
  toast.classList.remove('is-visible');
  if (hadFocus && toast.dataset.returnFocus) {
    const back = document.getElementById(toast.dataset.returnFocus);
    back?.focus({ preventScroll: true });
  }
}

/**
 * Where the toast sits. With no anchor: its usual spot (bottom centre on phones, bottom right from 990px).
 * With an anchor (the open ado AI chat panel): to its left with the bottoms aligned when a 320px toast fits there,
 * otherwise at the top edge, above the chat.
 * @param {HTMLElement} el
 * @param {DOMRect | null | undefined} anchor
 */
function placeToast(el, anchor) {
  el.classList.remove('ado-toast--beside', 'ado-toast--top');
  // The ado AI widget sets an inline z-index: 0 on page elements that overlap its UI; drop it with the placement.
  for (const prop of ['--ado-toast-right', '--ado-toast-bottom', '--ado-toast-width', 'z-index']) el.style.removeProperty(prop);
  if (!anchor) return;
  const gap = 16;
  const room = anchor.left - gap - 12;
  if (room >= 320) {
    el.classList.add('ado-toast--beside');
    el.style.setProperty('--ado-toast-right', `${Math.round(window.innerWidth - anchor.left + gap)}px`);
    el.style.setProperty('--ado-toast-bottom', `${Math.max(16, Math.round(window.innerHeight - anchor.bottom))}px`);
    el.style.setProperty('--ado-toast-width', `${Math.floor(Math.min(460, room))}px`);
  } else {
    el.classList.add('ado-toast--top');
  }
}

/**
 * @typedef {{ url: string, title: string, saving: string }} KitNudge
 * @param {{ title: string, image?: string, tone?: string, cartUrl: string, returnFocusId?: string, nudge?: KitNudge | null, anchor?: DOMRect | null }} info
 */
export function showToast({ title, image, tone, cartUrl, returnFocusId, nudge, anchor }) {
  const el = ensureToast();
  placeToast(el, anchor);
  const msg = el.querySelector('.ado-toast__msg');
  const thumb = el.querySelector('.ado-toast__thumb');
  const link = el.querySelector('.ado-toast__link');
  const nudgeLink = el.querySelector('.ado-toast__nudge');
  if (nudgeLink instanceof HTMLAnchorElement) {
    nudgeLink.hidden = !nudge;
    el.classList.toggle('ado-toast--nudge', Boolean(nudge));
    if (nudge) {
      nudgeLink.href = nudge.url;
      const text = nudgeLink.querySelector('.ado-toast__nudge-text');
      const save = nudgeLink.querySelector('.ado-toast__nudge-save');
      if (text) text.textContent = `Get all 3 as the ${nudge.title}`;
      if (save instanceof HTMLElement) {
        save.textContent = nudge.saving ? `Save ${nudge.saving}` : '';
        save.hidden = !nudge.saving;
      }
    }
  }
  if (thumb instanceof HTMLElement) {
    thumb.className = `ado-toast__thumb ado-toast__thumb--${tone || 'sky'}`;
    thumb.textContent = '';
    if (image) {
      const img = document.createElement('img');
      img.src = image;
      img.alt = '';
      img.width = 44;
      img.height = 44;
      img.decoding = 'async';
      thumb.append(img);
    }
  }
  if (link instanceof HTMLAnchorElement) link.href = cartUrl;
  if (returnFocusId) el.dataset.returnFocus = returnFocusId;
  if (msg) {
    msg.textContent = '';
    const kicker = document.createElement('span');
    kicker.className = 'ado-toast__kicker';
    kicker.textContent = 'Added to cart';
    const strong = document.createElement('strong');
    strong.textContent = title;
    msg.append(kicker, ' ', strong);
  }
  el.classList.remove('is-visible');
  // Restart the entrance even when a toast is already showing.
  void el.offsetWidth;
  el.classList.add('is-visible');
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(hideToast, TOAST_MS);
}

let uid = 0;

/** Routine step of a product handle, matched loosely like snippets/ado-product-meta.liquid. */
export function stepKey(/** @type {string} */ handle) {
  const h = (handle || '').toLowerCase();
  if (h.includes('kit')) return 'kit';
  if (h.includes('facewash') || h.includes('cleanser')) return 'facewash';
  if (h.includes('moistur')) return 'moisturiser';
  if (h.includes('sunscreen') || h.includes('spf')) return 'sunscreen';
  return '';
}

/**
 * The kit line for the toast: only while the bag has no kit and not yet all three steps.
 * @param {DOMStringMap} data
 * @param {{ items?: Array<{ handle?: string }> } | null} cart
 * @returns {KitNudge | null}
 */
function kitNudge(data, cart) {
  if (!data.kitUrl || !cart || !Array.isArray(cart.items)) return null;
  const steps = new Set();
  for (const item of cart.items) {
    const key = item.handle === data.kitHandle ? 'kit' : stepKey(item.handle || '');
    if (key === 'kit') return null;
    if (key) steps.add(key);
  }
  if (steps.size >= 3) return null;
  return { url: data.kitUrl, title: data.kitTitle || 'Tween’s Kit', saving: data.kitSaving || '' };
}

/** The card's product image to fly into the bag: the kit's middle tube, a tube cut-out, else the first image. */
function flightImage(/** @type {HTMLElement} */ host) {
  const card = host.closest('article, li, [data-ado-card]');
  if (!card) return null;
  for (const selector of ['.ado-kc__tube--2 img', '.ado-pv__tube img', '.ado-kc__photo img', 'img']) {
    const img = card.querySelector(selector);
    if (img) return img;
  }
  return null;
}

/** Waits for a promise, but never longer than ms (a hidden tab can hold an animation). */
function within(/** @type {Promise<void>} */ promise, /** @type {number} */ ms) {
  return Promise.race([promise, new Promise((resolve) => window.setTimeout(resolve, ms))]);
}

class AdoQuickAdd extends HTMLElement {
  constructor() {
    super();
    this.onSubmit = this.onSubmit.bind(this);
    this.onPageShow = this.onPageShow.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector('form[data-ado-quick-add]');
    if (!(this.form instanceof HTMLFormElement) || this.dataset.after === 'section') return;
    this.button = this.form.querySelector('[type="submit"]');
    this.label = this.querySelector('[data-ado-qa-label]');
    this.error = this.querySelector('[data-ado-qa-error]');
    this.originalLabel = this.label ? this.label.textContent : '';
    if (this.button && !this.button.id) this.button.id = `ado-qa-${Date.now().toString(36)}-${uid++}`;
    this.form.addEventListener('submit', this.onSubmit);
    window.addEventListener('pageshow', this.onPageShow);
    if (this.dataset.after !== 'cart') ensureToast();
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
    window.removeEventListener('pageshow', this.onPageShow);
    window.clearTimeout(this.resetTimer);
  }

  /** Back/forward cache: never come back to a spinning button. */
  onPageShow(/** @type {PageTransitionEvent} */ event) {
    if (event.persisted) this.reset();
  }

  reset() {
    window.clearTimeout(this.resetTimer);
    if (this.button) {
      this.button.classList.remove('is-loading', 'is-added');
      this.button.removeAttribute('aria-disabled');
    }
    if (this.label) this.label.textContent = this.originalLabel ?? '';
    this.removeAttribute('aria-busy');
  }

  /** @param {string} message */
  showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = false;
  }

  /** @param {SubmitEvent} event */
  async onSubmit(event) {
    const form = this.form;
    const button = this.button;
    if (!form || !button) return;
    event.preventDefault();
    if (button.getAttribute('aria-disabled') === 'true') return;

    const addUrl = `${this.dataset.addUrl || '/cart/add'}.js`;
    const cartUrl = this.dataset.cartUrl || '/cart';
    const data = new FormData(form);
    const id = Number(data.get('id'));
    const quantity = Number(data.get('quantity') || 1) || 1;

    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');
    this.setAttribute('aria-busy', 'true');
    if (this.error) {
      this.error.hidden = true;
      this.error.textContent = '';
    }

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id, quantity }] }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json.description || json.message || FALLBACK_ERROR);

      const cart = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
      const announceCart = () =>
        document.dispatchEvent(
          new CustomEvent('ado:cart:updated', {
            bubbles: true,
            detail: cart && typeof cart.item_count === 'number' ? { item_count: cart.item_count } : {},
          })
        );

      if (this.dataset.after === 'cart') {
        announceCart();
        window.location.assign(cartUrl);
        return;
      }

      button.classList.remove('is-loading');
      button.classList.add('is-added');
      if (this.label) this.label.textContent = 'Added';
      this.removeAttribute('aria-busy');
      this.resetTimer = window.setTimeout(() => this.reset(), ADDED_MS);

      // Burst on the button, the tube flies into the bag, the badge bumps as it lands, then the toast.
      /** @type {Promise<void>} */
      let landed = Promise.resolve();
      if (!reducedMotion()) {
        const source = flightImage(this);
        landed = import('@theme/ado-motion')
          .then((motion) => {
            motion.burst(button, { count: 8, spread: 60 });
            return motion.flyToCart(source);
          })
          .catch(() => {});
      }
      await within(landed, 1400);
      announceCart();
      showToast({
        title: this.dataset.title || 'Item',
        image: this.dataset.image,
        tone: this.dataset.tone,
        cartUrl,
        returnFocusId: button.id,
        nudge: kitNudge(this.dataset, cart),
      });
    } catch (error) {
      this.reset();
      this.showError(error instanceof Error && error.message ? error.message : FALLBACK_ERROR);
    }
  }
}

if (!customElements.get('ado-quick-add')) customElements.define('ado-quick-add', AdoQuickAdd);
